import { useCallback, useEffect, useRef, useState } from 'react';
import { listCustomerPanelTags, type CustomerPanelTag } from '../api/customerPanel';
import { PERSONAL_TAG_SCOPE } from './tagScope';
import type { InboxResponse } from '../api/inbox';

/**
 * Personal tags on the client. The privacy boundary is the server (every tag API answers with the
 * signed-in account's own tags only); this module makes sure the interface never shows anything
 * else either:
 *  - tag data is held per ACCOUNT: switching accounts resets it, and a response that was requested
 *    for a previous account is dropped when it arrives;
 *  - only responses marked with the personal scope are shown, so an older server's shared tags are
 *    never presented as "my tags".
 */

/** Inbox rows carry tags only from a personal-tag server; anything else is shown without tags. */
export function withPersonalRowTags(res: InboxResponse): InboxResponse {
  if (res.tagScope === PERSONAL_TAG_SCOPE) return res;
  return { ...res, rows: res.rows.map((row) => (row.tags?.length ? { ...row, tags: [] } : row)) };
}

export type MyTagsStatus = 'loading' | 'ready' | 'unavailable';

export interface MyTags {
  tags: CustomerPanelTag[];
  status: MyTagsStatus;
  reload: () => void;
  /** Add a tag this account just created (it is the account's own by construction). */
  remember: (tag: CustomerPanelTag) => void;
}

/** The signed-in account's own tags, reset on every account change. `agentId` null = nobody. */
export function useMyTags(agentId: string | null | undefined, { enabled = true } = {}): MyTags {
  const [state, setState] = useState<{ owner: string | null; tags: CustomerPanelTag[]; status: MyTagsStatus }>(
    { owner: agentId ?? null, tags: [], status: 'loading' },
  );
  const ownerRef = useRef(agentId ?? null);
  const abortRef = useRef<AbortController | null>(null);

  const reload = useCallback(() => {
    const owner = agentId ?? null;
    abortRef.current?.abort();
    if (!owner || !enabled) return;
    const controller = new AbortController();
    abortRef.current = controller;
    listCustomerPanelTags({ signal: controller.signal })
      .then((tags) => {
        if (ownerRef.current !== owner || controller.signal.aborted) return;   // another account by now
        setState({ owner, tags, status: 'ready' });
      })
      .catch(() => {
        if (ownerRef.current !== owner || controller.signal.aborted) return;
        // A failed refresh keeps the list it already has; a first load becomes "unavailable".
        setState((current) => (current.owner === owner && current.status === 'ready'
          ? current
          : { owner, tags: [], status: 'unavailable' }));
      });
  }, [agentId, enabled]);

  useEffect(() => {
    ownerRef.current = agentId ?? null;
    setState({ owner: agentId ?? null, tags: [], status: 'loading' });
    reload();
    return () => abortRef.current?.abort();
  }, [agentId, reload]);

  const remember = useCallback((tag: CustomerPanelTag) => {
    setState((current) => (current.owner !== (agentId ?? null) || current.tags.some((item) => item.id === tag.id)
      ? current
      : { ...current, tags: [...current.tags, tag] }));
  }, [agentId]);

  // Never hand out another account's state, even for the render between the change and the reset.
  const mine = state.owner === (agentId ?? null);
  return { tags: mine ? state.tags : [], status: mine ? state.status : 'loading', reload, remember };
}
